import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "../style/setup.css";
import Login from "./Login";
import axios from "../axios";
import Logo from "../componant/ui/Logo";
import {
  Check,
  Plus,
  Eye,
  Share2,
  ExternalLink,
  ImagePlus,
} from "lucide-react";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import ImageCropper from "../componant/ImageCropper";
import CoverEditor from "../componant/setup/CoverEditor";
import AvatarPicker from "../componant/setup/AvatarPicker";
import BioField from "../componant/setup/BioField";
import ThemePicker from "../componant/setup/ThemePicker";
import LinkList from "../componant/setup/LinkList";
import PhonePreview from "../componant/setup/PhonePreview";
import PreviewModal from "../componant/setup/PreviewModal";
import ShareModal from "../componant/setup/ShareModal";
import useLinkFields from "../hooks/useLinkFields";
import {
  LINK_IMAGE_ASPECT,
  LINK_IMAGE_OUTPUT_WIDTH,
  linkImageErrorMessage,
} from "../utils/linkImage";
import { fromApiLink, linkFieldsComplete, toLinkPayload } from "../utils/links";
import { getThemeSurface, getActiveTheme, loadThemeFont } from "../utils/themes";
import {
  COVER_ASPECT,
  COVER_MAX_WIDTH,
  coverUrl,
  validateCoverFile,
  uploadCover,
  removeCover,
  coverErrorMessage,
} from "../utils/cover";

function setToken(userToken) {
  localStorage.setItem("token", JSON.stringify(userToken));
}

function getToken() {
  const tokenString = localStorage.getItem("token");
  const userToken = JSON.parse(tokenString);
  return userToken?.token;
}

const Setup = () => {
  const { t } = useTranslation();
  const [imgchange, setimgchange] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const [bio, setbio] = useState();
  const [picture, setpicture] = useState(" ");
  const [showpicture, setshowpicture] = useState();
  const [modalIsOpen, setIsOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const [copy, setcopy] = useState(false);
  const [load, setload] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [havepic, sethavepic] = useState(false);

  const [theme, settheme] = useState("default");
  const [settings, setSettings] = useState(null); // may stay null for users without a settings row
  const [coverSrc, setCoverSrc] = useState(null);
  const [coverBusy, setCoverBusy] = useState(false);
  function openModal() {
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }
  const [id, setid] = useState();

  const {
    inputFields,
    setInputFields,
    dragIndex,
    linkCrop,
    handleFormChange,
    handleLinkBlur,
    remove,
    handleDragStart,
    handleDragEnd,
    handleDrop,
    addFields,
    addImageField,
    handleLinkImagePick,
    closeLinkCropper,
    handleLinkImageCropped,
  } = useLinkFields(t);

  let u = JSON.parse(localStorage.getItem("token"));

  const submit = (e) => {
    e.preventDefault();
    if (inputFields.some((field) => field.uploading)) {
      Swal.fire({ icon: "info", title: t("Please wait for the image to finish uploading"), text: "" });
      return;
    }
    if (linkFieldsComplete(inputFields)) {
      const formData = new FormData();
      formData.append("bio", bio);
      if (picture?.size || false) {
        formData.append("avatar", picture);
      }

      formData.append("username", u.user.username);

      // Reload only after both saves finish; reloading on the first response would cancel the link save.
      Promise.all([
        axios.post("/update?_method=put", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }),
        axios.post("/user/link", inputFields.map(toLinkPayload)),
      ])
        .then(() => {
          window.location.reload();
        })
        .catch((err) => {
          Swal.fire({ icon: "error", title: t(linkImageErrorMessage(err)), text: "" });
        });
    } else {
      Swal.fire({
        icon: "error",
        title: t("Make sure you fill in all the fields"),
        text: "",
      });
    }
  };

  //Choose a theme
  // Applies instantly; reverts and shows the backend message (e.g. 422 errors.theme) if the save fails.
  const chooseTheme = (name) => {
    const previous = theme;
    settheme(name);
    axios.put("/user/settings", { theme: name }).catch((err) => {
      settheme(previous);
      const data = err?.response?.data;
      Swal.fire({
        icon: "error",
        title: data?.errors?.theme?.[0] || data?.message || t("Could not save theme"),
        text: "",
      });
    });
  };
  const getdata = () => {
    setLoadError(false);
    axios
      .get("/user/link")
      .then(({ data }) => {
        const ary = data.links.map(fromApiLink);
        if (ary.length > 0) {
          setInputFields(ary);
        }
      })
      .catch(() => { });

    axios
      .get("/get/user")
      .then(({ data }) => {
        if (data.user.settings?.theme) settheme(data.user.settings.theme);
        setSettings(data.user.settings ?? null);
        if (data.user.bio !== "null") {
          setbio(data.user.bio);
        }
        if (data.user.image !== null) {
          sethavepic(true);
        }

        setpicture(data.user.image);

        setid(data.user.username);
        setTimeout(() => {
          setload(false);
        }, 10);
      })

      .catch((err) => {
        if (err?.response?.status === 401) {
          localStorage.removeItem("token");
        }
        setload(false);
        setLoadError(true);
      });
  };

  useEffect(() => {
    getdata();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadThemeFont(getActiveTheme(theme));
  }, [theme]);

  const token = getToken();
  if (!token) {
    return <Login setToken={setToken} />;
  }

  const activeTheme = getActiveTheme(theme);
  const themeSurface = getThemeSurface(activeTheme);

  // Picking a file opens the cropper; the cropped 512px JPEG is what gets uploaded.
  function handleImageUpload(event) {
    const file = event.target.files[0];
    event.target.value = ""; // allow re-picking the same file
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      Swal.fire({ icon: "error", title: t("Please choose an image file"), text: "" });
      return;
    }
    setCropSrc(URL.createObjectURL(file));
  }

  function closeCropper() {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
  }

  function handleCoverPick(event) {
    const file = event.target.files[0];
    event.target.value = "";
    if (!file) return;
    const problem = validateCoverFile(file);
    if (problem) {
      Swal.fire({ icon: "error", title: t(problem), text: "" });
      return;
    }
    setCoverSrc(URL.createObjectURL(file));
  }

  function closeCoverCropper() {
    if (coverSrc) URL.revokeObjectURL(coverSrc);
    setCoverSrc(null);
  }

  function handleCoverCropped(file) {
    setCoverBusy(true);
    uploadCover(file)
      .then(({ data }) => {
        setSettings(data.settings);
        if (data.settings?.theme) settheme(data.settings.theme);
        closeCoverCropper();
      })
      .catch((err) => {
        Swal.fire({ icon: "error", title: t(coverErrorMessage(err)), text: "" });
      })
      .finally(() => setCoverBusy(false));
  }

  function handleCoverRemove() {
    setCoverBusy(true);
    removeCover()
      .then(({ data }) => setSettings(data.settings))
      .catch((err) => {
        Swal.fire({ icon: "error", title: t(coverErrorMessage(err)), text: "" });
      })
      .finally(() => setCoverBusy(false));
  }

  function handleCropped(file) {
    setimgchange(true);
    setpicture(file);
    setshowpicture(URL.createObjectURL(file));
    closeCropper();
  }

  // Props shared by the desktop and mobile copies of each form section.
  const coverProps = {
    url: coverUrl(settings),
    busy: coverBusy,
    onPick: handleCoverPick,
    onRemove: handleCoverRemove,
    t,
  };
  const avatarProps = { imgchange, havepic, picture, showpicture, onPick: handleImageUpload };
  const linkListProps = {
    inputFields,
    dragIndex,
    onChange: handleFormChange,
    onLinkBlur: handleLinkBlur,
    onPickImage: handleLinkImagePick,
    onRemove: remove,
    onDragStart: handleDragStart,
    onDragEnd: handleDragEnd,
    onDrop: handleDrop,
    t,
  };
  const previewProps = {
    activeTheme,
    themeSurface,
    imgchange,
    havepic,
    picture,
    showpicture,
    id,
    bio,
    inputFields,
    cover: coverUrl(settings),
    t,
  };

  return (
    <div className=" back bg-gradient-to-br from-slate-100 via-indigo-50 to-purple-100 flex flex-col h-full min-h-screen pt-8 px-4 lg:px-10 pb-16 items-center flex-wrap">
      {cropSrc && (
        <ImageCropper
          src={cropSrc}
          onConfirm={handleCropped}
          onCancel={closeCropper}
        />
      )}
      {coverSrc && (
        <ImageCropper
          src={coverSrc}
          aspect={COVER_ASPECT}
          shape="rect"
          outputWidth={COVER_MAX_WIDTH}
          quality={0.85}
          fileName="cover.jpg"
          title={t("Crop cover")}
          uploading={coverBusy}
          onConfirm={handleCoverCropped}
          onCancel={closeCoverCropper}
        />
      )}
      {linkCrop && (
        <ImageCropper
          src={linkCrop.src}
          aspect={LINK_IMAGE_ASPECT}
          shape="rect"
          outputWidth={LINK_IMAGE_OUTPUT_WIDTH}
          quality={0.85}
          fileName="link.jpg"
          title={t("Crop image")}
          onConfirm={handleLinkImageCropped}
          onCancel={closeLinkCropper}
        />
      )}
      {load ? (
        <div style={{ margin: "auto" }}>
          <Logo animated="loader" className="w-24" title={t("Loading")} />
        </div>
      ) : loadError ? (
        <div className="m-auto text-center max-w-sm">
          <p className="text-slate-600 font-medium mb-4">
            {t("Something went wrong loading your profile. Please check your connection and try again.")}
          </p>
          <button
            type="button"
            onClick={() => {
              setload(true);
              getdata();
            }}
            className="inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl px-6 py-3 text-sm font-semibold shadow-lg shadow-indigo-600/30 transition"
          >
            {t("Retry")}
          </button>
        </div>
      ) : (
        <>
          <div className="hidden lg:flex w-full max-w-8xl  px-6 md:px-10 items-start gap-10 mb-20">
            {/* Form column */}
            <div className="flex-1 min-w-0 bg-white rounded-3xl shadow-xl shadow-indigo-950/5 ring-1 ring-slate-900/5 p-8 xl:p-10">
              <form onSubmit={submit} method="post">
                <CoverEditor {...coverProps} />
                <div className="space-y-6">
                  <div className="flex items-end justify-between gap-4 px-1 -mt-12">
                    <AvatarPicker {...avatarProps} />
                  </div>

                  {/* User Bio */}
                  <BioField bio={bio} onChange={setbio} t={t} />

                  <ThemePicker theme={theme} onChoose={chooseTheme} t={t} />

                  <LinkList {...linkListProps} />

                  <div className="flex items-center gap-3 mt-6">
                    <button
                      type="button"
                      onClick={addFields}
                      className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50/50 rounded-xl py-3 text-sm font-semibold transition"
                    >
                      <Plus className="w-4 h-4 shrink-0" /> {t("Add")}
                    </button>
                    <button
                      type="button"
                      onClick={addImageField}
                      className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50/50 rounded-xl py-3 text-sm font-semibold transition"
                    >
                      <ImagePlus className="w-4 h-4 shrink-0" /> {t("Add image")}
                    </button>
                    <button
                      type="submit"
                      onClick={submit}
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-3 text-sm font-semibold shadow-lg shadow-indigo-600/30 transition"
                    >
                      <Check className="w-4 h-4 shrink-0" /> {t("Submit")}
                    </button>
                  </div>
                </div>
              </form>

              <div className="mt-4 flex items-center gap-3">
                <Link
                  to="/en/show"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl py-3 text-sm font-semibold transition"
                >
                  <Eye className="w-4 h-4 shrink-0" /> {t("View as visitor")}
                </Link>
                <button
                  onClick={openModal}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-black text-white rounded-xl py-3 text-sm font-semibold transition"
                >
                  <Share2 className="w-4 h-4 shrink-0" /> {t("Share")}
                </button>
              </div>
            </div>

            {/* Live phone preview column */}
            <div className="w-[300px] xl:w-[340px]  shrink-0 sticky top-8">
              <PhonePreview {...previewProps} />
              <p className="text-center text-xs font-medium text-slate-400 mt-4">
                {t("Live preview · updates as you type")}
              </p>
            </div>
          </div>

          {/* mobile & tablet */}

          <div className="lg:hidden w-full max-w-xl sm:max-w-2xl mx-auto">
            <div className="bg-white rounded-3xl shadow-xl shadow-indigo-950/5 ring-1 ring-slate-900/5 p-5 sm:p-8">
              <form onSubmit={submit} method="post">
                <CoverEditor {...coverProps} />
                <div className="space-y-5 sm:space-y-6">
                  <div className="flex items-end justify-between gap-4 px-1 -mt-12">
                    <AvatarPicker {...avatarProps} />
                    <button
                      type="button"
                      onClick={() => setPreviewOpen(true)}
                      className="shrink-0 inline-flex items-center gap-2 bg-slate-900 hover:bg-black text-white rounded-xl px-4 py-2.5 text-xs font-semibold shadow-md transition"
                    >
                      <Eye className="w-4 h-4 shrink-0" /> {t("Preview")}
                    </button>
                  </div>

                  {/* User Bio */}
                  <BioField bio={bio} onChange={setbio} t={t} />

                  <ThemePicker theme={theme} onChoose={chooseTheme} t={t} />

                  <LinkList {...linkListProps} compact />

                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-6">
                    <button
                      type="button"
                      onClick={addFields}
                      className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50/50 rounded-xl py-3 text-sm font-semibold transition"
                    >
                      <Plus className="w-4 h-4 shrink-0" /> {t("Add")}
                    </button>
                    <button
                      type="button"
                      onClick={addImageField}
                      className="flex-1 inline-flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 hover:bg-indigo-50/50 rounded-xl py-3 text-sm font-semibold transition"
                    >
                      <ImagePlus className="w-4 h-4 shrink-0" /> {t("Add image")}
                    </button>
                    <button
                      type="submit"
                      onClick={submit}
                      className="col-span-2 sm:col-span-1 inline-flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl py-3 text-sm font-semibold shadow-lg shadow-indigo-600/30 transition"
                    >
                      <Check className="w-4 h-4 shrink-0" /> {t("Submit")}
                    </button>
                  </div>
                </div>
              </form>

              <div className="flex items-center gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => setPreviewOpen(true)}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl py-3 text-sm font-semibold transition"
                >
                  <Eye className="w-4 h-4 shrink-0" /> {t("Preview")}
                </button>
                <button
                  onClick={openModal}
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-slate-900 hover:bg-black text-white rounded-xl py-3 text-sm font-semibold transition"
                >
                  <Share2 className="w-4 h-4 shrink-0" /> {t("Share")}
                </button>
              </div>
              <Link
                to="/en/show"
                className="mt-3 w-full inline-flex items-center justify-center gap-2 border border-slate-200 hover:border-indigo-400 hover:text-indigo-600 text-slate-700 rounded-xl py-3 text-sm font-semibold transition"
              >
                <ExternalLink className="w-4 h-4 shrink-0" /> {t("View as visitor")}
              </Link>
            </div>
          </div>
        </>
      )}
      {load ? (
        <p></p>
      ) : (
        <>
          <PreviewModal isOpen={previewOpen} onClose={() => setPreviewOpen(false)} t={t}>
            <PhonePreview {...previewProps} />
          </PreviewModal>

          <ShareModal
            isOpen={modalIsOpen}
            onClose={closeModal}
            username={u?.user?.username}
            copied={copy}
            onCopy={() => setcopy(true)}
            t={t}
          />
        </>
      )}
    </div>
  );
};

export default Setup;
